/**
 * Colmore: directional sticky for the PDP buy column.
 *
 * `position: sticky; top: <header offset>` pins the TOP of the column. When the column is
 * taller than the viewport — a logged-in B2B page with a staffel table easily is, e.g. 1768px
 * against a 700px window — its lower half can never be read: it stays pinned until the whole
 * grid has scrolled past, and only then slides up. Reported 2026-08-10.
 *
 * The fix keeps `position: sticky` and moves its `top` with the scroll DIRECTION, clamped to
 * [viewportHeight - elementHeight, headerOffset]:
 *
 *   scrolling down — `top` decreases until the column's bottom edge reaches the viewport
 *                    bottom, so the column finishes scrolling before the page continues;
 *   scrolling up   — `top` increases until the column's top edge reaches the header offset,
 *                    the exact inverse.
 *
 * Between those bounds the element is stuck at the current `top` while we shift `top` by the
 * scroll delta, which reads as the column scrolling with the page. Sticky itself still
 * handles the ends of the containing block, so nothing needs to be recomputed there.
 *
 * NOTE ON THE SCROLLER: above 990px `html:has(.page-wrapper)` is `overflow: hidden` and
 * `.page-wrapper` is the scrolling element, so `window.scrollY` never moves and listening on
 * `window` would silently do nothing. Resolved at bind time and re-resolved on resize.
 */

const SELECTOR = '.product-details.sticky-content--desktop';
const DESKTOP = '(min-width: 750px)';

function init() {
  const element = document.querySelector(SELECTOR);
  if (!element) return;

  const desktop = window.matchMedia(DESKTOP);
  let scroller = null;
  let lastScroll = 0;
  let top = 0;
  let frame = 0;

  /** The page wrapper scrolls above 990px; below that it is the window. */
  function resolveScroller() {
    const wrapper = document.querySelector('.page-wrapper');
    if (!wrapper) return null;
    const overflow = getComputedStyle(wrapper).overflowY;
    const scrolls = overflow === 'auto' || overflow === 'scroll';
    return scrolls && wrapper.scrollHeight > wrapper.clientHeight ? wrapper : null;
  }

  const scrollTop = () => (scroller ? scroller.scrollTop : window.scrollY);
  const viewportHeight = () => (scroller ? scroller.clientHeight : window.innerHeight);

  /** Set by a `body:has(...)` rule when the header is in its sticky state, so read it live. */
  function headerOffset() {
    const value = parseFloat(getComputedStyle(element).getPropertyValue('--sticky-header-offset'));
    return Number.isFinite(value) ? value : 0;
  }

  function update() {
    frame = 0;

    const offset = headerOffset();
    const available = viewportHeight() - offset;

    // Short enough to pin normally, or stacked on mobile: hand back to the stylesheet.
    if (!desktop.matches || element.offsetHeight <= available) {
      element.style.removeProperty('top');
      top = offset;
      lastScroll = scrollTop();
      return;
    }

    const min = viewportHeight() - element.offsetHeight; // bottom edge at viewport bottom
    const max = offset; // top edge under the header
    const current = scrollTop();

    top = Math.min(max, Math.max(min, top - (current - lastScroll)));
    lastScroll = current;
    element.style.top = `${top}px`;
  }

  function onScroll() {
    if (!frame) frame = requestAnimationFrame(update);
  }

  function bind() {
    (scroller ?? window).removeEventListener('scroll', onScroll);
    scroller = resolveScroller();
    lastScroll = scrollTop();
    top = headerOffset();
    (scroller ?? window).addEventListener('scroll', onScroll, { passive: true });
  }

  bind();
  update();

  window.addEventListener(
    'resize',
    () => {
      bind();
      update();
    },
    { passive: true }
  );
  desktop.addEventListener('change', () => {
    bind();
    update();
  });

  // The column grows and shrinks in use — accordions, variant swaps, the staffel table.
  new ResizeObserver(() => onScroll()).observe(element);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
